"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "@/types/fleet";
import { loginRequest, logoutRequest, meRequest } from "@/lib/api/auth";
import { ApiClientError } from "@/lib/api/http";
import { flushActivity, setActivityLoggingEnabled } from "@/lib/activity/emit-store";

interface AuthContextType {
  user: User | null;
  /** Same bearer token as used for API auth; for WebSocket query param. Null when logged out. */
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  lastErrorCode: string | null;
  login: (
    username: string,
    password: string,
  ) => Promise<{ ok: boolean; errorCode?: string }>;
  logout: () => void;
  /** Re-reads the session from `/api/auth/me`, e.g. after the user edits their own profile. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastErrorCode, setLastErrorCode] = useState<string | null>(null);
  // The shared lists (vehicles, groups, costs, …) are keyed without a user id, so whatever the
  // previous session cached would be served to the next one. Every session boundary drops it.
  const queryClient = useQueryClient();

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      try {
        const session = await meRequest();
        if (!isMounted) return;
        setUser(session.user);
        setToken(session.token);
        setActivityLoggingEnabled(true);
      } catch {
        if (!isMounted) return;
        setUser(null);
        setToken(null);
        setActivityLoggingEnabled(false);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<{ ok: boolean; errorCode?: string }> => {
    setLastErrorCode(null);
    try {
      const session = await loginRequest(username, password);
      // Also covers a session that ended without logout() (expired cookie, other tab), and anything
      // a still-mounted view refetched between logout() and the cookie delete.
      queryClient.clear();
      setUser(session.user);
      setToken(session.token);
      setActivityLoggingEnabled(true);
      return { ok: true };
    } catch (error) {
      setUser(null);
      setToken(null);
      setActivityLoggingEnabled(false);
      if (error instanceof ApiClientError) {
        setLastErrorCode(error.code);
        return { ok: false, errorCode: error.code };
      } else {
        setLastErrorCode("request_failed");
        return { ok: false, errorCode: "request_failed" };
      }
    }
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    // Anything still queued belongs to the session that is ending, and the flush needs the cookie
    // that `logoutRequest()` is about to delete — so it goes first, before the state is torn down.
    flushActivity();
    setActivityLoggingEnabled(false);
    // The session state is torn down only after the cookie delete has landed. Dropping the user
    // first sends DashboardLayout to /login while the cookie is still valid, and middleware bounces
    // an authenticated /login straight back — leaving the tab stuck on a spinner. It would also let
    // the still-mounted views refetch the ending session's data back into the cache.
    void logoutRequest()
      .catch(() => {
        // Signed out locally regardless; a cookie that survived is caught by the next API call.
      })
      .finally(() => {
        setUser(null);
        setToken(null);
        setLastErrorCode(null);
        queryClient.clear();
      });
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    try {
      const session = await meRequest();
      setUser(session.user);
      setToken(session.token);
    } catch {
      // A failed refresh leaves the current session as it was; an expired one is caught by the
      // next API call like everywhere else.
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        lastErrorCode,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
